// src/components/SignFinder.jsx
import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import TarotCard from './TarotCard';
import LoadingSpinner from './ui/LoadingSpinner';
import { getSignByDateString, renderSafeMarkdown } from '../utils/zodiacUtils';

const SignFinder = ({ aiStatus, userDetails, onViewFullSign }) => {
  const cards = useSelector((state) => state.tarot.cards);
  const [birthDate, setBirthDate] = useState(userDetails?.birthDate || '');
  const [sign, setSign] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [horoscope, setHoroscope] = useState('');
  const [isLoadingHoroscope, setIsLoadingHoroscope] = useState(false);
  const [horoscopeError, setHoroscopeError] = useState('');
  const [horoscopeCached, setHoroscopeCached] = useState(false);

  const aiOnline = aiStatus === 'online';
  const tarotCard = sign ? cards.find((c) => c.name === sign.tarotCard) : null;

  const handleCalculate = (e) => {
    e.preventDefault();
    const found = getSignByDateString(birthDate);
    setSign(found);
    setNotFound(!found);
    setHoroscope('');
    setHoroscopeError('');
    setHoroscopeCached(false);
  };

  const handleRequestHoroscope = async () => {
    if (!sign) return;
    setIsLoadingHoroscope(true);
    setHoroscopeError('');
    setHoroscope('');
    setHoroscopeCached(false);
    try {
      const response = await fetch('/api/generateHoroscope', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          signName: sign.name,
          tarotCardName: sign.tarotCard,
        }),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.message || 'Error desconocido del Oráculo AI');
      setHoroscope(data.horoscope);
      setHoroscopeCached(!!data.cached);
    } catch (err) {
      setHoroscopeError(`No se pudo generar el horóscopo: ${err.message}`);
    } finally {
      setIsLoadingHoroscope(false);
    }
  };

  return (
    <div className="zodiac-tool-card">
      <h3 className="zodiac-section-title font-cinzel">¿Cuál es mi signo?</h3>
      <p className="zodiac-tool-desc">
        Ingresá tu fecha de nacimiento para descubrir tu signo solar, la carta de tarot que lo representa y tu horóscopo del día.
      </p>
      <form className="zodiac-tool-form" onSubmit={handleCalculate}>
        <input
          type="date"
          value={birthDate}
          onChange={(e) => setBirthDate(e.target.value)}
          required
          className="guide-search-input zodiac-date-input"
        />
        <button type="submit" className="main-button primary-button">Descubrir mi signo</button>
      </form>

      {notFound && <p className="zodiac-horoscope-error">No pudimos calcular tu signo con esa fecha. Probá de nuevo.</p>}

      {sign && (
        <div className="zodiac-tool-result">
          <div className="zodiac-tool-result-header">
            <span className="zodiac-tool-result-symbol">{sign.symbol}</span>
            <div>
              <h4 className="font-cinzel">{sign.name}</h4>
              <p className="zodiac-card-element">{sign.element} · {sign.modality} · {sign.rulingPlanetSymbol} {sign.rulingPlanet}</p>
            </div>
          </div>
          <p className="zodiac-tool-result-desc">{sign.description}</p>
          <button className="main-button secondary-button" onClick={() => onViewFullSign(sign)}>
            Ver ficha completa
          </button>

          {tarotCard && (
            <div className="zodiac-sign-tarot">
              <h4 className="meaning-label">🎴 La carta de tarot de {sign.name}</h4>
              <div className="zodiac-sign-tarot-layout">
                <TarotCard card={tarotCard} isFlipped={true} />
                <p className="zodiac-tool-result-desc">
                  <strong>{tarotCard.name}:</strong> {tarotCard.meaning_up}
                </p>
              </div>
            </div>
          )}

          <div className="zodiac-horoscope-section">
            {!horoscope && !isLoadingHoroscope && (
              <button
                className="main-button primary-button oracle-trigger-btn"
                onClick={handleRequestHoroscope}
                disabled={!aiOnline}
                title={!aiOnline ? 'El Oráculo AI no está disponible en este momento' : ''}
              >
                <span className="oracle-btn-shimmer"></span>
                🔮 {aiOnline ? 'Ver horóscopo de hoy para tu signo' : 'Oráculo AI no disponible'}
              </button>
            )}
            {isLoadingHoroscope && <LoadingSpinner message="Consultando al Oráculo..." />}
            {horoscopeError && <p className="zodiac-horoscope-error">{horoscopeError}</p>}
            {horoscope && (
              <div className="zodiac-horoscope-result">
                {horoscopeCached && <p className="zodiac-horoscope-tag">✨ Horóscopo del día</p>}
                {renderSafeMarkdown(horoscope)}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default SignFinder;
